import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, CheckCircle2, Circle, Clock, Calendar,
  Trash2, Edit3, Search, Loader2, AlertTriangle, Flag, ArrowRight,
  BarChart3, Target, TrendingUp, Zap
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
// TASKS-V2 — novos componentes + helpers (atrás de feature flags).
import HeroFocusCard from "@/components/tasks/HeroFocusCard";
import MobileCollapse from "@/components/shared/MobileCollapse";  // FaseB B1
import NotificationBell from "@/components/shared/NotificationBell";  // FaseC C1
import TaskGroup from "@/components/tasks/TaskGroup";
// PRODUTO-V1 M2 — bloco "Recomendado pelo Nexo".
import RecommendedBlock from "@/components/tasks/RecommendedBlock";
// PRODUTO-V1 — Modo Foco overlay.
import FocusMode from "@/components/focus/FocusMode";
import { groupTasks, GROUP_ORDER, GROUP_LABELS } from "@/utils/taskGrouping";
import { formatCountdown, URGENCY_COLOR, formatAbsoluteTooltip } from "@/utils/taskCountdown";
import trace from "@/services/executionTrace";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// TASKS-V2 — feature flags. Default true; setar "false" no .env do
// frontend pra rollback granular sem deploy.
const FLAG_HERO = (process.env.REACT_APP_TASKS_HERO_FOCUS ?? "true") !== "false";
const FLAG_GROUPS = (process.env.REACT_APP_TASKS_TEMPORAL_GROUPS ?? "true") !== "false";
const FLAG_COUNTDOWN = (process.env.REACT_APP_TASKS_COUNTDOWN ?? "true") !== "false";
// PRODUTO-V1 M2 — bloco "Recomendado pelo Nexo". Default true.
const FLAG_RECOMMENDED = (process.env.REACT_APP_TASKS_RECOMMENDED ?? "true") !== "false";
// PRODUTO-V1 — Modo Foco. Default true.
const FLAG_FOCUS_MODE = (process.env.REACT_APP_FOCUS_MODE ?? "true") !== "false";

/**
 * Security audit (FIN-2 T1, 2026-04-30) — sections legacy.
 *
 * Backend está blindado pela SEC-1 (scoped_filter via JWT). Esta tela usa
 * fetch puro, então sem token todos os endpoints retornam 401 e a UI quebra.
 * Não há vazamento — só fadiga de UX em deep link sem login.
 *
 * | Endpoint chamado            | authFetch usado? | Notas             |
 * |-----------------------------|------------------|-------------------|
 * | GET  /api/tasks             | ❌ → ✅ (T2)     | listar tasks      |
 * | POST /api/tasks             | ❌ → ✅ (T2)     | criar task        |
 * | POST /api/tasks/status      | ❌ → ✅ (T2)     | mudar status      |
 * | DEL  /api/tasks/:id         | ❌ → ✅ (T2)     | deletar           |
 * | PUT  /api/tasks/:id         | ❌ → ✅ (T2)     | editar            |
 */

const statusConfig = {
  todo: { label: "A fazer", color: "#64748b", bg: "bg-slate-500/10", text: "text-slate-400", icon: Circle },
  doing: { label: "Em andamento", color: "#06b6d4", bg: "bg-cyan-500/10", text: "text-cyan-400", icon: Clock },
  done: { label: "Concluído", color: "#22c55e", bg: "bg-emerald-500/10", text: "text-emerald-400", icon: CheckCircle2 },
};

const priorityConfig = {
  high: { label: "Alta", color: "#ef4444", bg: "bg-red-500/10", text: "text-red-400" },
  medium: { label: "Média", color: "#f59e0b", bg: "bg-amber-500/10", text: "text-amber-400" },
  low: { label: "Baixa", color: "#64748b", bg: "bg-slate-500/10", text: "text-slate-500" },
};

function StatsCard({ icon: Icon, value, label, color, subtitle }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="glass-card glow-hover rounded-2xl p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-white tabular-nums">{value}</h3>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
      {subtitle && <p className="text-[11px] text-slate-600 mt-2 pl-[52px]">{subtitle}</p>}
    </motion.div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0f1629] border border-white/10 rounded-lg px-3 py-2 shadow-2xl">
      <p className="text-xs text-slate-400 mb-1">{label}</p>
      {payload.map((e, i) => (
        <p key={i} className="text-sm font-semibold" style={{ color: e.fill || e.color }}>{e.value} tarefas</p>
      ))}
    </div>
  );
}

function TaskCard({ task, onStatusChange, onDelete, onEdit }) {
  const status = statusConfig[task.status] || statusConfig.todo;
  const priority = priorityConfig[task.priority] || priorityConfig.medium;
  const StatusIcon = status.icon;
  const isOverdue = task.datetime_due && new Date(task.datetime_due || task.due_date) < new Date() && task.status !== "done";
  const nextStatus = task.status === "todo" ? "doing" : task.status === "doing" ? "done" : "todo";

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const now = new Date();
    const diffD = Math.floor((d - now) / 86400000);
    if (diffD === 0) return "Hoje";
    if (diffD === 1) return "Amanhã";
    if (diffD === -1) return "Ontem";
    if (diffD < -1) return `${Math.abs(diffD)} dias atrás`;
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  };

  return (
    <motion.div layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -12 }}
      className={`flex items-start gap-3 px-4 py-3.5 rounded-xl transition-all group hover:bg-white/[0.015]
        ${task.status === "done" ? "opacity-40" : isOverdue ? "bg-red-500/[0.03]" : ""}`}
      data-testid={`task-card-${task.id}`}>
      <button onClick={() => onStatusChange(task, nextStatus)}
        className="mt-0.5 transition-all hover:scale-110 flex-shrink-0" style={{ color: status.color }}
        data-testid={`task-toggle-${task.id}`}>
        <StatusIcon className="w-5 h-5" />
      </button>
      <div className="flex-1 min-w-0">
        <h4 className={`text-sm font-medium ${task.status === "done" ? "line-through text-slate-500" : "text-white"}`}>{task.title}</h4>
        {task.description && <p className="text-xs text-slate-600 mt-0.5 line-clamp-1">{task.description}</p>}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {(task.datetime_due || task.due_date) && (() => {
            // TASKS-V2 E3 — countdown relativo ("vence em 3h", "atrasada há 2 dias")
            // substitui a data absoluta quando FLAG_COUNTDOWN ativa.
            if (!FLAG_COUNTDOWN) {
              return (
                <span className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md ${isOverdue ? "bg-red-500/10 text-red-400" : "bg-white/[0.03] text-slate-500"}`}>
                  {isOverdue && <AlertTriangle className="w-3 h-3" />}
                  <Calendar className="w-3 h-3" />
                  {formatDate(task.datetime_due || task.due_date)}
                </span>
              );
            }
            const cd = formatCountdown(task.datetime_due || task.due_date);
            if (!cd) return null;
            const cls = URGENCY_COLOR[cd.urgency] || "text-slate-500";
            const bg = cd.urgency === "overdue" ? "bg-red-500/10" : "bg-white/[0.03]";
            return (
              <span
                className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md ${bg} ${cls}`}
                title={formatAbsoluteTooltip(task.datetime_due || task.due_date)}
                data-testid={`task-countdown-${task.id}`}
              >
                <Clock className="w-3 h-3" />
                {cd.text}
              </span>
            );
          })()}
          <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${status.bg} ${status.text}`}>{status.label}</span>
          {task.priority && task.priority !== "medium" && (
            <span className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ${priority.bg} ${priority.text}`}>
              <Flag className="w-3 h-3" /> {priority.label}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => onEdit(task)} className="p-1.5 rounded-lg text-slate-600 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"><Edit3 className="w-3.5 h-3.5" /></button>
        <button onClick={() => onDelete(task)} className="p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </motion.div>
  );
}

function CreateTaskModal({ isOpen, onClose, onSubmit, editTask = null }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("medium");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editTask) {
      setTitle(editTask.title || ""); setDescription(editTask.description || "");
      const d = editTask.datetime_due || editTask.due_date || "";
      setDueDate(d.split("T")[0] || ""); setPriority(editTask.priority || "medium");
    } else { setTitle(""); setDescription(""); setDueDate(""); setPriority("medium"); }
  }, [editTask, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault(); if (!title.trim()) return;
    setIsSubmitting(true);
    await onSubmit({ title, description, due_date: dueDate || null, datetime_due: dueDate ? `${dueDate}T23:59:00` : null, priority });
    setIsSubmitting(false); onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()} className="bg-[#0d1220] border border-white/[0.08] rounded-2xl p-6 safe-modal-pb w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-5">{editTask ? "Editar Tarefa" : "Nova Tarefa"}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Título</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="O que você precisa fazer?"
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors" autoFocus />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Descrição</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detalhes..." rows={2}
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 transition-colors resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Data</label>
                  <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:border-cyan-500/30 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Prioridade</label>
                  <select value={priority} onChange={(e) => setPriority(e.target.value)}
                    className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:border-cyan-500/30 transition-colors">
                    <option value="low">Baixa</option><option value="medium">Média</option><option value="high">Alta</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-3 rounded-xl border border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/5 transition-colors">Cancelar</button>
                <button type="submit" disabled={!title.trim() || isSubmitting}
                  className="flex-1 px-4 py-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 disabled:opacity-40 transition-all flex items-center justify-center gap-2 font-medium">
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />} {editTask ? "Salvar" : "Criar"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function TasksSection() {
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  // PRODUTO-V1 — Modo Foco overlay state.
  const [focusTask, setFocusTask] = useState(null);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/tasks`);
      if (res.ok) {
        const data = await res.json();
        const allTasks = data.all_tasks || [...(data.today || []), ...(data.future || []), ...(data.overdue || [])];
        const unique = allTasks.filter((t, i, s) => i === s.findIndex(x => x.id === t.id));
        setTasks(unique);
      }
    } catch (err) { console.error("Error fetching tasks:", err); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleCreate = async (data) => {
    try {
      const res = await authFetch(`${API_URL}/api/tasks`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
      if (res.ok) { toast.success("Tarefa criada!"); fetchTasks(); }
    } catch (err) { toast.error("Erro ao criar"); }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      const res = await authFetch(`${API_URL}/api/tasks/status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: task.id, status: newStatus }),
      });
      if (res.ok) {
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus, is_completed: newStatus === "done" } : t));
        toast.success(newStatus === "done" ? "Concluída!" : "Status atualizado");
      }
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (task) => {
    if (!window.confirm("Excluir esta tarefa?")) return;
    try {
      const res = await authFetch(`${API_URL}/api/tasks/${task.id}`, { method: "DELETE" });
      if (res.ok) { setTasks(prev => prev.filter(t => t.id !== task.id)); toast.success("Excluída"); }
    } catch (err) { console.error(err); }
  };

  const handleEdit = (task) => { setEditingTask(task); setIsModalOpen(true); };
  const handleUpdate = async (data) => {
    try {
      const res = await authFetch(`${API_URL}/api/tasks/${editingTask.id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...editingTask, ...data }),
      });
      if (res.ok) { toast.success("Atualizada!"); fetchTasks(); setEditingTask(null); }
    } catch (err) { toast.error("Erro"); }
  };

  const filteredTasks = tasks.filter(t => {
    const matchFilter = filter === "all" || t.status === filter || (filter === "overdue" && t.datetime_due && new Date(t.datetime_due) < new Date() && t.status !== "done");
    const matchSearch = !searchQuery || t.title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchFilter && matchSearch;
  });

  // TASKS-V2 — trace de render por mount/filter (1× por mudança de filter/total).
  // Não inclui search pra evitar burst de eventos enquanto user digita.
  useEffect(() => {
    if (isLoading) return;
    try {
      const counts = FLAG_GROUPS ? Object.fromEntries(
        Object.entries(groupTasks(filteredTasks)).map(([k, v]) => [k, v.length])
      ) : {};
      trace.record("tasks.list.rendered", {
        total_count: filteredTasks.length,
        filter,
        group_counts: counts,
      });
    } catch { /* */ }
  }, [filter, isLoading, tasks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    const now = new Date();
    return {
      total: tasks.length,
      done: tasks.filter(t => t.status === "done" || t.is_completed).length,
      doing: tasks.filter(t => t.status === "doing").length,
      overdue: tasks.filter(t => t.datetime_due && new Date(t.datetime_due) < now && t.status !== "done" && !t.is_completed).length,
    };
  }, [tasks]);

  const progress = stats.total > 0 ? (stats.done / stats.total) * 100 : 0;

  const statusChartData = useMemo(() => [
    { name: "A fazer", value: tasks.filter(t => t.status === "todo" && !t.is_completed).length, color: "#64748b" },
    { name: "Fazendo", value: stats.doing, color: "#06b6d4" },
    { name: "Concluído", value: stats.done, color: "#22c55e" },
    { name: "Atrasado", value: stats.overdue, color: "#ef4444" },
  ].filter(d => d.value > 0), [tasks, stats]);

  return (
    <div className="p-6 md:p-8 pb-32 max-w-5xl mx-auto" data-testid="tasks-section">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-wide">Tarefas</h1>
          <p className="text-sm text-slate-500 mt-1">{stats.total} total &middot; {stats.done} concluídas</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <NotificationBell />
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => { setEditingTask(null); setIsModalOpen(true); }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 hover:bg-cyan-500/15 transition-all text-sm font-medium" data-testid="create-task-btn">
            <Plus className="w-4 h-4" /> Nova Tarefa
          </motion.button>
        </div>
      </div>

      {/* TASKS-V2 E1 — Hero "Foco de Hoje". FaseB B1: colapsado em mobile
          pra liberar espaço pra lista; expandido em desktop como antes. */}
      {FLAG_HERO && !isLoading && (
        <MobileCollapse title="FOCO DE HOJE" defaultOpen={false} desktopTitle={false} className="mb-6">
          <HeroFocusCard
            tasks={tasks}
            onStart={(t) => handleStatusChange(t, "doing")}
            onComplete={(t) => handleStatusChange(t, "done")}
            onAskNexo={() => {
              // Próxima sessão pode amplificar — por agora só telemetria
              // já é registrada pelo HeroFocusCard. Toast de feedback:
              toast.info("Pergunte ao Nexo no chat: 'o que devo priorizar?'");
            }}
            // PRODUTO-V1 — entry pro Modo Foco. Quando flag off, ausência
            // do prop esconde o botão no HeroFocusCard.
            onEnterFocus={FLAG_FOCUS_MODE ? (t) => setFocusTask(t) : undefined}
          />
        </MobileCollapse>
      )}

      {/* PRODUTO-V1 M2 — Recomendado pelo Nexo (top 3 via briefing_ranker) */}
      {FLAG_RECOMMENDED && !isLoading && (
        <RecommendedBlock
          refreshKey={tasks.length}
          onStart={(t) => handleStatusChange(t, "doing")}
          onComplete={(t) => handleStatusChange(t, "done")}
        />
      )}

      {/* Stats + Chart Row. FaseB B1: gap menor em mobile pra dar fôlego. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 md:gap-3 mb-6">
        <StatsCard icon={Target} value={stats.total} label="Total" color="#64748b" />
        <StatsCard icon={Clock} value={stats.doing} label="Em andamento" color="#06b6d4" />
        <StatsCard icon={CheckCircle2} value={stats.done} label="Concluídas" color="#22c55e" subtitle={stats.total > 0 ? `${Math.round(progress)}% do total` : undefined} />
        <StatsCard icon={AlertTriangle} value={stats.overdue} label="Atrasadas" color="#ef4444" />

        {/* Mini Chart */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-2xl p-4 col-span-2 lg:col-span-1" data-testid="tasks-status-chart">
          <p className="text-xs text-slate-500 mb-2">Distribuição</p>
          {statusChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={64}>
              <BarChart data={statusChartData} layout="vertical" margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" hide />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="value" radius={[4, 4, 4, 4]} barSize={14}>
                  {statusChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-600 text-center mt-3">Sem dados</p>}
        </motion.div>
      </div>

      {/* Progress Bar */}
      {stats.total > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-slate-500">Progresso geral</span>
            <span className="text-cyan-400 font-medium">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 bg-white/[0.04] rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 rounded-full" />
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
          <input type="text" placeholder="Buscar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-cyan-500/30 text-sm transition-colors" data-testid="tasks-search" />
        </div>
        {/* FaseB B1: scroll horizontal com snap em mobile (era flex normal que
            cortava "Atrasadas" no 390px); flex-wrap-livre em desktop como antes. */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide snap-x snap-mandatory -mx-6 px-6 md:overflow-x-visible md:mx-0 md:px-0 md:snap-none">
          {[{ id: "all", label: "Todas" }, { id: "todo", label: "A fazer" }, { id: "doing", label: "Fazendo" }, { id: "done", label: "Concluídas" }, { id: "overdue", label: "Atrasadas" }].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className={`shrink-0 snap-start whitespace-nowrap px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f.id ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
              data-testid={`task-filter-${f.id}`}>{f.label}
              {f.id === "overdue" && stats.overdue > 0 && <span className="ml-1 text-red-400">({stats.overdue})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Tasks List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
      ) : filteredTasks.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-7 h-7 text-cyan-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-1.5">{searchQuery ? "Nenhuma tarefa encontrada" : "Nenhuma tarefa"}</h3>
          <p className="text-slate-600 text-sm mb-5">{searchQuery ? "Tente outro termo" : "Crie sua primeira tarefa"}</p>
          {!searchQuery && <button onClick={() => setIsModalOpen(true)} className="px-5 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-sm hover:bg-cyan-500/15 transition-all">Criar Tarefa</button>}
        </motion.div>
      ) : FLAG_GROUPS ? (
        <div data-testid="tasks-list-grouped">
          {(() => {
            // TASKS-V2 E2 — agrupamento temporal. groupTasks aplica
            // classifyTask + sort (priority desc, due asc, created_at asc).
            // Filtros existentes ("a fazer", "fazendo", etc) já foram
            // aplicados em filteredTasks acima.
            const grouped = groupTasks(filteredTasks);
            return GROUP_ORDER.map((g) => {
              const items = grouped[g] || [];
              if (items.length === 0) return null;
              const defaultCollapsed = g === "done";
              return (
                <TaskGroup
                  key={g}
                  groupId={g}
                  count={items.length}
                  defaultCollapsed={defaultCollapsed}
                >
                  <AnimatePresence mode="popLayout">
                    {items.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onStatusChange={handleStatusChange}
                        onDelete={handleDelete}
                        onEdit={handleEdit}
                      />
                    ))}
                  </AnimatePresence>
                </TaskGroup>
              );
            });
          })()}
        </div>
      ) : (
        <div className="glass-card rounded-2xl divide-y divide-white/[0.03]" data-testid="tasks-list">
          <AnimatePresence mode="popLayout">
            {filteredTasks.map(task => (
              <TaskCard key={task.id} task={task} onStatusChange={handleStatusChange} onDelete={handleDelete} onEdit={handleEdit} />
            ))}
          </AnimatePresence>
        </div>
      )}

      <CreateTaskModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingTask(null); }}
        onSubmit={editingTask ? handleUpdate : handleCreate} editTask={editingTask} />

      {/* PRODUTO-V1 — Modo Foco overlay. State local, sem backend. */}
      {FLAG_FOCUS_MODE && (
        <FocusMode
          task={focusTask}
          isOpen={!!focusTask}
          onClose={() => setFocusTask(null)}
          onCompleteTask={(t) => handleStatusChange(t, "done")}
        />
      )}
    </div>
  );
}
